import React from 'react';
import PropTypes from 'prop-types';
import { ApiDataAccess } from '../../../infrastructure/ApiDataAccess';
import { ToolbarButton, ToolbarControl } from '../../../controls/ToolbarControl';
import './StandardProductPricingComponent.css';

// --- StandardProductPricingComponent ---

const MAX_QUANTITY_BREAKS = 10;
const defaultQuantityBreaks = [100, 500, 1000, 1500, 2000, 3000, 5000, 7500, 9000, 10000];

export class StandardProductPricingComponent extends React.Component {
    constructor(props) {
        super(props);
        this.dataAccess = this.props.dataAccess || new ApiDataAccess("/data");
        this.state = {
            additionalSkuPrice: null,
            breakdowns: [],
            loaded: false,
            quantityBreakInput: '',
            isSaving: false,
            isAddingQB: false,
            hasChanged: false,
            error: null
        };

        this.loadAllPricing = this.loadAllPricing.bind(this);
        this.retrieveNewQuantityBreakPricing = this.retrieveNewQuantityBreakPricing.bind(this);
        this.savePrices = this.savePrices.bind(this);
        this.handlePriceUpdate = this.handlePriceUpdate.bind(this);
        this.handleQuantityBreakUpdate = this.handleQuantityBreakUpdate.bind(this);
        this.handleDeleteItem = this.handleDeleteItem.bind(this);
    }

    componentDidMount() {
        this.loadAllPricing();
    }

    loadAllPricing() {
        const standardProductId = this.props.param_0;
        this.dataAccess.post(`/standard-product/${encodeURIComponent(standardProductId)}/pricing`, defaultQuantityBreaks)
            .then(data => {
                console.log("Fetched Pricing Data: ", data);
                this.setState({
                    additionalSkuPrice: data.additionalSkuPrice,
                    breakdowns: this.formatBreakdownPrices(data.breakdowns),
                    loaded: true,
                    error: null
                });
            })
            .catch(error => {
                console.log("error: ", error)
                if (error) {
                    var errorObj = JSON.parse(error);
                    this.setState({
                        loaded: true,
                        additionalSkuPrice: null,
                        breakdowns: [],
                        error: errorObj.detail
                    });
                }
            });
    }

    formatBreakdownPrices = (items) => {
        if (!items || !Array.isArray(items)) return [];
        const initializedData = items.map(item => {
            const dynamicsPrice = parseFloat(item.dynamicsUnitPrice);
            let initialUserPriceValue = dynamicsPrice;

            const userPriceProp = item.userEnteredUnitPrice;
            if (userPriceProp !== null && userPriceProp !== undefined && userPriceProp !== '') {
                const parsedUserPrice = parseFloat(userPriceProp);
                if (!isNaN(parsedUserPrice)) {
                    initialUserPriceValue = parsedUserPrice;
                }
            }
            return {
                ...item,
                userEnteredUnitPrice: initialUserPriceValue === null || isNaN(initialUserPriceValue) ? null : initialUserPriceValue,
            };
        });
        const sortedData = initializedData.sort((a, b) => a.quantityBreak - b.quantityBreak);
        return sortedData;
    }


    addNewQuantityBreak = (newOrUpdatedPriceObject) => {
        if (!newOrUpdatedPriceObject || typeof newOrUpdatedPriceObject.quantityBreak === 'undefined') {
            console.error("Cannot add invalid price object:", newOrUpdatedPriceObject);
            return;
        }
        this.setState(prevState => {
            const currentPrices = Array.isArray(prevState.breakdowns) ? prevState.breakdowns : [];
            const newQuantityBreak = newOrUpdatedPriceObject.quantityBreak;
            const newDynamicsPrice = parseFloat(newOrUpdatedPriceObject.dynamicsUnitPrice);

            const existingIndex = currentPrices.findIndex(
                item => item.quantityBreak === newQuantityBreak
            );

            let updatedList;

            if (existingIndex > -1) {
                updatedList = currentPrices.map((item, index) => {
                    const resolvedUpdate = {
                        ...newOrUpdatedPriceObject,
                        userEnteredUnitPrice: newOrUpdatedPriceObject.userEnteredUnitPrice !== undefined ?
                            (newOrUpdatedPriceObject.userEnteredUnitPrice === null || isNaN(parseFloat(newOrUpdatedPriceObject.userEnteredUnitPrice)) ? null : parseFloat(newOrUpdatedPriceObject.userEnteredUnitPrice))
                            : item.userEnteredUnitPrice
                    };
                    return index === existingIndex ? resolvedUpdate : item;
                });
            } else {
                console.log("Adding new QB:", newQuantityBreak);
                const newItemForState = {
                    ...newOrUpdatedPriceObject,
                    userEnteredUnitPrice: isNaN(newDynamicsPrice) ? null : newDynamicsPrice
                };
                updatedList = [...currentPrices, newItemForState];
            }
            const sortedList = updatedList.sort((a, b) => a.quantityBreak - b.quantityBreak);

            return {
                breakdowns: sortedList,
                hasChanged: true
            };
        });
    };


    retrieveNewQuantityBreakPricing() {
        const quantity = Number(this.state.quantityBreakInput);
        if (!quantity || quantity <= 0 || !Number.isInteger(quantity)) {
            alert("Please enter a positive whole number quantity break.");
            return;
        }
        const standardProductId = this.props.param_0;
        this.setState({ isAddingQB: true });
        this.dataAccess.post(`/standard-product/${encodeURIComponent(standardProductId)}/pricing/new`, quantity)
            .then(data => {
                if (data) {
                    this.addNewQuantityBreak(data);
                }
                this.setState({
                    quantityBreakInput: ''
                });
            })
            .catch(error => {
                console.error("Error retrieving new QB pricing:", error);
                alert("Failed to add new quantity break pricing.");
            })
            .finally(() => {
                this.setState({ isAddingQB: false });
            });

    }


    handlePriceUpdate(qb, priceValue) {
        this.setState(prevState => {
            const currentBreakdowns = Array.isArray(prevState.breakdowns) ? prevState.breakdowns : [];
            const updatedBreakdowns = currentBreakdowns.map(item => {
                if (item.quantityBreak === qb) {
                    return { ...item, userEnteredUnitPrice: priceValue };
                }
                return item;
            });
            return {
                breakdowns: updatedBreakdowns,
                hasChanged: true
            };
        });
    }

    handleQuantityBreakUpdate(e) {
        this.setState({ quantityBreakInput: e.target.value });
    }


    handleDeleteItem(quantityBreakToDelete) {
        console.log("Marking QB for deletion:", quantityBreakToDelete);
        this.setState(prevState => {
            const currentBreakdowns = Array.isArray(prevState.breakdowns) ? prevState.breakdowns : [];
            const updatedBreakdowns = currentBreakdowns.map(item => {
                if (item.quantityBreak === quantityBreakToDelete) {
                    return {
                        ...item,
                        isMarkedForDeletion: true // Add the flag
                    };
                }
                // Otherwise, return the item unchanged
                return item;
            });
            return {
                breakdowns: updatedBreakdowns,
                hasChanged: true
            };
        });
    }


    savePrices() {
        const standardProductId = this.props.param_0;

        if (!this.state.breakdowns || this.state.breakdowns.length === 0) {
            alert("No pricing data to process.");
            return;
        }

        const itemsToSaveOrUpdate = [];
        const itemsToDelete = [];

        this.state.breakdowns.forEach(item => {
            if (item.isMarkedForDeletion) {
                itemsToDelete.push(item.quantityBreak);
            } else {
                itemsToSaveOrUpdate.push({
                    QuantityBreak: item.quantityBreak,
                    DynamicsUnitPrice: item.dynamicsUnitPrice,
                    DynamicsMarginPercentage: item.dynamicsMarginPercentage || 0,
                    UserEnteredUnitPrice: item.userEnteredUnitPrice,
                    UnitCost: (item.customDynamicsTotalCost / item.quantityBreak)
                });
            }
        });

        //console.log("Items to Save/Update DTO: ", JSON.stringify(itemsToSaveOrUpdate, null, 2));
        //console.log("Items to Delete (Identifiers): ", itemsToDelete);


        const payload = {
            updates: itemsToSaveOrUpdate,
            deletedQuantityBreaks: itemsToDelete
        };

        this.setState({ isSaving: true });
        this.dataAccess.post(`/standard-product/${encodeURIComponent(standardProductId)}/pricing/save`, payload)
            .then(data => {
                //console.log("Save batch response: ", data);
                window.Alerts.showSuccess('Prices saved successfully!', 'Standard Product Pricing');
                this.setState({
                    isSaving: false,
                    hasChanged: false
                });
            })
            .catch(error => {
                console.error("Error saving prices:", error);

                window.Alerts.ShowError(`Your pricing was not sumbitted. Please try again. ${error.message || ""}`, 'Standard Product Pricing');
                this.setState({ isSaving: false });
                // Consider if you need to revert the 'isMarkedForDeletion' flags on error
            });
    }

    render() {
        const { loaded, breakdowns, additionalSkuPrice, quantityBreakInput, isSaving, hasChanged, error } = this.state;
        const breakdownsForTable = Array.isArray(breakdowns) ? breakdowns : [];
        const activeQuantityBreaksCounter = (breakdownsForTable.filter(x => !x.isMarkedForDeletion));
        const isAddButtonDisabled = quantityBreakInput === '' || Number(quantityBreakInput) <= 0 || !Number.isInteger(Number(quantityBreakInput)) || activeQuantityBreaksCounter.length >= MAX_QUANTITY_BREAKS;

        if (!loaded) { return <div className="price-loading">Loading pricing data from Dynamics...</div>; }

        return (
            <div>
                <div>
                    <ToolbarControl renderButtons={() => {
                        return (
                            <div>
                                <ToolbarButton icon='glyphicon-step-backward' text='Back' onClick={() => window.Router.setRoute('/standard-products/' + encodeURIComponent(this.props.param_0))} />
                            </div>
                        )
                    }} />
                </div>
                <h1>Unit Price Configuration</h1>

                {breakdownsForTable.length > 0 ? (
                    <div>

                        <div className="pricing-toolbar-container">
                            <div className="toolbar-left">
                                <input
                                    type="number" step="1" min="1" pattern="\d*"
                                    className={'quantity-break-input'}
                                    value={quantityBreakInput}
                                    onChange={this.handleQuantityBreakUpdate}
                                    placeholder="Enter Quantity Break"
                                    disabled={isSaving}
                                    aria-label="Enter Quantity Break"
                                />
                                <button
                                    className={'btn btn-primary btn-tb'}
                                    disabled={isAddButtonDisabled || isSaving}
                                    onClick={this.retrieveNewQuantityBreakPricing}
                                    style={{ marginLeft: '8px' }}
                                >
                                    <span className='text'> Add </span>
                                </button>

                                {this.state.isAddingQB && <span className="loader-inline"></span>}
                            </div>
                            <div className="toolbar-right">
                                <button
                                    className={'btn btn-success'}
                                    onClick={this.savePrices}
                                    disabled={isSaving || breakdownsForTable.length === 0}
                                >
                                    {isSaving ? 'Saving...' : 'Save Prices'}
                                </button>
                            </div>
                        </div>

                        {hasChanged &&
                            <div className="alert alert-info" role="alert">
                                <span className="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>
                                Changes are not saved until you click Save Prices
                            </div>
                        }

                        <hr />
                        <ItemPriceTable
                            items={breakdownsForTable}
                            additionalSkuPrice={additionalSkuPrice}
                            onUnitPriceChange={this.handlePriceUpdate}
                            onDeleteItem={this.handleDeleteItem}
                            disabled={isSaving}
                        />
                    </div>
                ) : (
                    <div>
                        {error &&
                            <div className="alert alert-danger" role="alert">
                                <span className="glyphicon glyphicon-exclamation-sign" aria-hidden="true"></span>
                                {error}
                            </div>
                        }
                    </div>
                )}

            </div>
        );
    }
}

// --- CHILD COMPONENT (ItemPriceTable) ---

function ItemPriceTable({
    items = [],
    additionalSkuPrice = 0,
    onUnitPriceChange = () => { },
    onDeleteItem = () => { },
    disabled = false
}) {


    const handleUnitPriceInputChange = (event, qb) => {
        const value = event.target.value;
        let processedValue = null;

        if (value) {
            const parsed = parseFloat(value);
            if (!isNaN(parsed)) {
                processedValue = parsed;
            }
        }

        onUnitPriceChange(qb, processedValue);
    };

    const formatCurrency = (value) => {
        if (value === null || typeof value === 'undefined')
            return '';

        const number = parseFloat(value);
        if (isNaN(number))
            return '';

        return number.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 });
    };

    const formatMargin = (value) => {
        if (value === null || typeof value === 'undefined')
            return '';

        const number = parseFloat(value);
        if (isNaN(number))
            return '';

        return `${number.toFixed(2)}%`;
    };

    const handleDeleteRow = (quantityBreakToDelete) => {
        onDeleteItem(quantityBreakToDelete);
    };

    const displayItems = items.filter(item => !item.isMarkedForDeletion);

    return (
        <table className="item-price-table">
            <thead>
                <tr>
                    <th></th>
                    <th>Quantity Break</th>
                    <th>Standard Unit Cost</th>
                    <th>Standard Unit Price</th>
                    <th>Standard Margin %</th>
                    <th>Custom Unit Cost</th>
                    <th>Custom Unit Price</th>
                    <th>Custom Margin %</th>
                    <th>Additional Sku Price</th>                    
                    <th>Unit Price</th>
                    <th>&nbsp;</th>
                </tr>
            </thead>
            <tbody>
                {displayItems.length === 0 ? (
                    <tr><td colSpan="6">No price data available.</td></tr>
                ) : (
                    displayItems.map((row) => {
                        const priceValue = row.userEnteredUnitPrice;
                        const isValidPrice = row.userEnteredUnitPrice !== null && row.userEnteredUnitPrice > 0;

                        const inputClassName = `quantity-break-input unit-price-input ${isValidPrice ? 'unit-price-input--valid' : ''}`;

                        return (
                            <tr key={row.id || row.quantityBreak}>
                                <td>
                                    <button className='btn btn-primary btn-sm btn-danger' onClick={() => handleDeleteRow(row.quantityBreak)} disabled={disabled}>  <span className={'glyphicon glyphicon-trash'} /> </button>
                                </td>
                                <td className="read-only-cell">{row.quantityBreak}</td>
                                <td className="read-only-cell currency">{formatCurrency(row.dynamicsTotalCost / row.quantityBreak)}</td>
                                <td className="read-only-cell currency">{formatCurrency(row.dynamicsUnitPrice)}</td>
                                <td className="read-only-cell margin">{formatMargin(row.dynamicsMarginPercentage)}</td>

                                <td className="read-only-cell currency">{formatCurrency(row.customDynamicsTotalCost / row.quantityBreak)}</td>
                                <td className="read-only-cell currency">{formatCurrency(row.customDynamicsUnitPrice)}</td>
                                <td className="read-only-cell margin">{formatMargin(row.customDynamicsMarginPercentage)}</td>

                                <td className="read-only-cell margin">{formatCurrency(additionalSkuPrice)}</td>
                                <td className="input-cell">
                                    <input
                                        type="number"
                                        step="any"
                                        min="0"
                                        className={inputClassName}
                                        value={priceValue === null || priceValue === undefined ? '' : priceValue}
                                        onChange={(e) => handleUnitPriceInputChange(e, row.quantityBreak)}
                                        placeholder="Enter price"
                                        disabled={disabled}
                                        aria-label={`Unit price for quantity break ${row.quantityBreak}`}
                                        required
                                    />
                                </td>

                                <td className="read-only-cell margin">
                                    {row.pricingStatus === "SF" ? 
                                        <span title="Price not synced">
                                            ⚠️
                                        </span>
                                        :
                                        <span title="Ok">
                                            ✅
                                        </span>
                                    }
                                </td>
                            </tr>
                        );
                    })
                )}
            </tbody>
        </table>
    );
}

// Add PropTypes if needed

StandardProductPricingComponent.propTypes = {
    param_0: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    dataAccess: PropTypes.object,
    initialItems: PropTypes.array
};

ItemPriceTable.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        quantityBreak: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        dynamicsUnitPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        dynamicsMarginPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]),
        userEnteredUnitPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])])
    })).isRequired,
    onUnitPriceChange: PropTypes.func.isRequired,
    onDeleteItem: PropTypes.func.isRequired,
    disabled: PropTypes.bool
};


export default StandardProductPricingComponent;